import { FC, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { Button, Layout, Avatar, Dropdown, Tooltip, Typography } from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import CurrencyService from "@/services/currency.service";
import AccountService from "@/services/account.service";
import { currencyBadge, partnerAccountCurrency } from "@/utils/currencyBadge";
import { useLocation, useNavigate } from "react-router-dom";
import Quote from "../components/Quote";
import { routes } from "@/config/routes";
import cn from "classnames";
const { Header } = Layout;

type Props = {
  isSmallDevice: boolean;
  collapsed: boolean;
  onBurgerClick: () => void;
};

const MainHeader: FC<Props> = ({ isSmallDevice, collapsed, onBurgerClick }) => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const user = auth.user;
  const { pathname } = useLocation();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleProfile = useCallback(() => {
    navigate("/profile");
  }, [navigate]);

  const userMenuItems = useMemo(
    () => [
      {
        key: "profile",
        icon: <ProfileOutlined />,
        label: "Profile",
        onClick: handleProfile,
      },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Logout",
        onClick: handleLogout,
      },
    ],
    [handleProfile, handleLogout]
  );

  const showQuoteSelector = useMemo(() => {
    return pathname.includes(routes.products.root) || pathname.includes(routes.comandCenter.products.root);
  }, [pathname]);

  /**
   * The permanent currency badge (2026-08-19). Same query keys and staleTime as
   * the products page's, so this shares those cache entries rather than adding
   * requests, and `own-account` stays gated on there being more than one active
   * currency — a USD-only deployment issues exactly the requests it always did
   * and shows no badge, where "Prices in USD" would be noise, not information.
   */
  const { data: activeCurrencies } = useQuery({
    queryKey: ["activeCurrencies"],
    queryFn: () => CurrencyService.getActiveCurrencies(),
    staleTime: 5 * 60 * 1000,
  });
  const activeCodes = useMemo(
    () => (activeCurrencies?.data?.currencies ?? []).map((c: { code: string }) => c.code),
    [activeCurrencies]
  );

  const { data: ownAccount } = useQuery({
    queryKey: ["own-account"],
    queryFn: () => AccountService.getOwnAccount(),
    staleTime: 5 * 60 * 1000,
    enabled: activeCodes.length > 1,
  });

  /**
   * Null for an INTERNAL user on purpose: they transact in no single currency, so
   * a badge would be wrong more often than right. See utils/currencyBadge.ts.
   */
  const badge = useMemo(
    () => (activeCodes.length > 1
      ? currencyBadge(partnerAccountCurrency({
          accountType: ownAccount?.data?.accountType ?? user?.accountType,
          accountCurrency: ownAccount?.data?.currency,
          activeCodes,
        }))
      : null),
    [activeCodes, ownAccount, user?.accountType]
  );

  const [scrollY, setScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const isSticky = useMemo(() => {
    return (pathname.includes(routes.products.root) || pathname.includes(routes.comandCenter.products.root)) && scrollY > 100;
  }, [pathname, scrollY]);

  return (
    <Header className={cn(styles.header, { [styles.sticky]: isSticky })}>
      <div className={styles.headerLeft}>
        {isSmallDevice && (
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onBurgerClick}
          />
        )}
        {showQuoteSelector && (
          <div className={styles.quoteSelector}>
            <Quote />
          </div>
        )}
      </div>
      <div className={styles.headerRight}>
        {badge && (
          <Tooltip title={badge.title}>
            <Typography.Text type="secondary" className={styles.currencyBadge}>
              {badge.label}
            </Typography.Text>
          </Tooltip>
        )}
        {/* <div className={styles.rightBox}>
                    <Badge count={1}>
                        <Avatar className={styles.avatarBox} icon={<BellOutlined />} size={26} />
                    </Badge>
                </div> */}
        {user && (
          <div className={styles.rightBox}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar
                className={styles.avatarBox}
                icon={<UserOutlined />}
                alt={`${user.firstName} ${user.lastName}`}
              />
            </Dropdown>
          </div>
        )}
      </div>
    </Header>
  );
};

export default MainHeader;
